import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Save, Code, AlertCircle, LogOut } from 'lucide-react';

const TrackingCodes = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [codes, setCodes] = useState({
    global_header_code: '',
    global_footer_code: '',
    checkout_success_code: '',
    landing_page_code: ''
  });

  useEffect(() => {
    if (!user || user.role !== 'super_admin') {
      navigate('/login');
      return;
    }
    fetchTrackingCodes();
  }, [user, navigate]);

  const fetchTrackingCodes = async () => {
    try {
      const response = await api.get('/admin/tracking-codes');
      setCodes({
        global_header_code: response.data.global_header_code || '',
        global_footer_code: response.data.global_footer_code || '',
        checkout_success_code: response.data.checkout_success_code || '',
        landing_page_code: response.data.landing_page_code || ''
      });
    } catch (error) {
      console.error('Failed to fetch tracking codes:', error);
      setMessage({ type: 'error', text: 'Failed to load tracking codes' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await api.put('/admin/tracking-codes', codes);
      setMessage({ type: 'success', text: 'Tracking codes saved successfully!' });
    } catch (error) {
      console.error('Failed to save tracking codes:', error);
      setMessage({ type: 'error', text: 'Failed to save tracking codes' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-black shadow-sm border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <img src="https://cdn.slicksignature.com/landing/slicksignature-logo.png" alt="SlickSignature" className="h-12" />
              <div>
                <h1 className="text-xl font-bold text-white">Tracking Codes</h1>
                <p className="text-sm text-gray-400">Analytics & Conversion Tracking</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-400">
                {user?.email}
              </span>
              <Button
                onClick={() => navigate('/admin')}
                variant="outline"
                size="sm"
                className="text-white border-white hover:bg-gray-800"
              >
                Back to Admin
              </Button>
              <Button 
                onClick={logout} 
                variant="outline" 
                size="sm"
                className="text-white border-white hover:bg-gray-800"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        {message.text && (
          <Alert className={`mb-6 ${message.type === 'success' ? 'bg-green-900 border-green-700' : 'bg-red-900 border-red-700'}`}>
            <AlertDescription className="text-white">
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Analytics & Tracking Codes</CardTitle>
            <CardDescription>
              Add tracking codes (Google Analytics, Facebook Pixel, etc.) to your pages. Use HTML script tags.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Global Header Code */}
            <div>
              <Label className="flex items-center space-x-2 mb-2">
                <span>Global Header Code</span>
                <span className="text-xs text-gray-500">(Injected in &lt;head&gt; on all pages)</span>
              </Label>
              <Textarea
                value={codes.global_header_code}
                onChange={(e) => setCodes({ ...codes, global_header_code: e.target.value })}
                placeholder={`<script async src="https://www.googletagmanager.com/gtag/js?id=GA_MEASUREMENT_ID"></script>\n<script>\n  window.dataLayer = window.dataLayer || [];\n  function gtag(){dataLayer.push(arguments);}\n  gtag('js', new Date());\n  gtag('config', 'GA_MEASUREMENT_ID');\n</script>`}
                rows={8}
                className="font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Common use: Google Analytics, Google Tag Manager, Meta Pixel
              </p>
            </div>

            {/* Global Footer Code */}
            <div>
              <Label className="flex items-center space-x-2 mb-2">
                <span>Global Footer Code</span>
                <span className="text-xs text-gray-500">(Injected before &lt;/body&gt; on all pages)</span>
              </Label>
              <Textarea
                value={codes.global_footer_code}
                onChange={(e) => setCodes({ ...codes, global_footer_code: e.target.value })}
                placeholder={`<script>\n  // Add any footer scripts here\n</script>`}
                rows={6}
                className="font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Common use: Chat widgets, additional tracking scripts
              </p>
            </div>

            {/* Checkout Success Page Code */}
            <div className="border-t border-gray-200 pt-6">
              <Label className="flex items-center space-x-2 mb-2">
                <span>Checkout Success Page Code</span>
                <span className="text-xs text-orange-600">(Only on /checkout/success)</span>
              </Label>
              <Textarea
                value={codes.checkout_success_code}
                onChange={(e) => setCodes({ ...codes, checkout_success_code: e.target.value })}
                placeholder={`<script>\n  // Conversion tracking\n  gtag('event', 'conversion', {\n    'send_to': 'AW-CONVERSION_ID/CONVERSION_LABEL',\n    'value': 1.0,\n    'currency': 'USD'\n  });\n</script>`}
                rows={8}
                className="font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Common use: Google Ads conversion tracking, Facebook conversion pixel
              </p>
            </div>

            {/* Landing Page Code */}
            <div className="border-t border-gray-200 pt-6">
              <Label className="flex items-center space-x-2 mb-2">
                <span>Landing Page Code</span>
                <span className="text-xs text-orange-600">(Only on / homepage)</span>
              </Label>
              <Textarea
                value={codes.landing_page_code}
                onChange={(e) => setCodes({ ...codes, landing_page_code: e.target.value })}
                placeholder={`<script>\n  // Landing page specific tracking\n</script>`}
                rows={6}
                className="font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Common use: Landing page-specific analytics, heat mapping tools
              </p>
            </div>

            {/* Warning */}
            <Alert className="bg-yellow-50 border-yellow-200">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800 text-sm">
                <strong>Important:</strong> Make sure your tracking codes are valid HTML/JavaScript. Invalid code may break your pages.
                Test thoroughly after adding new codes.
              </AlertDescription>
            </Alert>

            {/* Save Button */}
            <div className="flex justify-end pt-4">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Tracking Codes
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TrackingCodes;
